import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set auth header if token exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Dashboard
export const getDashboardStats = () => axios.get(`${API}/dashboard/stats`);
export const getRecentActivities = (limit = 10) => axios.get(`${API}/dashboard/recent-activities?limit=${limit}`);
export const getSalesChart = (days = 30) => axios.get(`${API}/dashboard/sales-chart?days=${days}`);

// Materials
export const getMaterials = (params) => axios.get(`${API}/materials`, { params });
export const getMaterial = (id) => axios.get(`${API}/materials/${id}`);
export const createMaterial = (data) => axios.post(`${API}/materials`, data);
export const updateMaterial = (id, data) => axios.put(`${API}/materials/${id}`, data);
export const updateStock = (id, quantityChange) => axios.patch(`${API}/materials/${id}/stock?quantity_change=${quantityChange}`);

// Suppliers
export const getSuppliers = () => axios.get(`${API}/suppliers`);
export const getSupplier = (id) => axios.get(`${API}/suppliers/${id}`);
export const createSupplier = (data) => axios.post(`${API}/suppliers`, data);

// Clients
export const getClients = (search) => axios.get(`${API}/clients`, { params: { search } });
export const getClient = (id) => axios.get(`${API}/clients/${id}`);
export const createClient = (data) => axios.post(`${API}/clients`, data);
export const updateClient = (id, data) => axios.put(`${API}/clients/${id}`, data);

// Client Interactions
export const getInteractions = (clientId) => axios.get(`${API}/clients/${clientId}/interactions`);
export const createInteraction = (clientId, data) => axios.post(`${API}/clients/${clientId}/interactions`, data);

// Quotations (File Upload Based)
export const getQuotations = (params) => axios.get(`${API}/quotations`, { params });
export const getQuotation = (id) => axios.get(`${API}/quotations/${id}`);
export const uploadQuotation = (file, clientId, title, totalValue, validityDays = 30, notes = '') => {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams({
    client_id: clientId,
    title: title,
    total_value: totalValue.toString(),
    validity_days: validityDays.toString()
  });
  if (notes) params.append('notes', notes);
  return axios.post(`${API}/quotations/upload?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const updateQuotationStatus = (id, status) => axios.patch(`${API}/quotations/${id}/status?status=${status}`);

// Earned Value Management (EVM)
export const getEVMProjects = () => axios.get(`${API}/evm/projects`);
export const getEVMProject = (id) => axios.get(`${API}/evm/projects/${id}`);
export const createEVMProject = (data) => axios.post(`${API}/evm/projects`, data);
export const getWorkPackages = (projectId) => axios.get(`${API}/evm/projects/${projectId}/work-packages`);
export const createWorkPackage = (projectId, data) => axios.post(`${API}/evm/projects/${projectId}/work-packages`, data);
export const updateWorkPackage = (wpId, data) => axios.patch(`${API}/evm/work-packages/${wpId}`, data);
export const getEVMSnapshots = (projectId) => axios.get(`${API}/evm/projects/${projectId}/snapshots`);
export const getSCurveData = (projectId) => axios.get(`${API}/evm/projects/${projectId}/s-curve`);
export const getEVMAnalysis = (projectId) => axios.get(`${API}/evm/projects/${projectId}/analysis`);

// Jobs
export const getJobs = (params) => axios.get(`${API}/jobs`, { params });
export const getJob = (id) => axios.get(`${API}/jobs/${id}`);
export const createJob = (data) => axios.post(`${API}/jobs`, data);
export const updateJobStatus = (id, status) => axios.patch(`${API}/jobs/${id}/status?status=${status}`);
export const updateJobProgress = (id, progress) => axios.patch(`${API}/jobs/${id}/progress?progress=${progress}`);

// Production
export const getProductionItems = (params) => axios.get(`${API}/production`, { params });
export const getProductionItem = (id) => axios.get(`${API}/production/${id}`);
export const getProductionByBarcode = (barcode) => axios.get(`${API}/production/barcode/${barcode}`);
export const createProductionItem = (data) => axios.post(`${API}/production`, data);
export const updateProductionStage = (id, data) => axios.patch(`${API}/production/${id}/stage`, data);

// Purchase Orders
export const getPurchaseOrders = (params) => axios.get(`${API}/purchase-orders`, { params });
export const getPurchaseOrder = (id) => axios.get(`${API}/purchase-orders/${id}`);
export const createPurchaseOrder = (data) => axios.post(`${API}/purchase-orders`, data);
export const updatePOStatus = (id, status) => axios.patch(`${API}/purchase-orders/${id}/status?status=${status}`);
export const receiveDelivery = (poId, materialId, quantity) => 
  axios.post(`${API}/purchase-orders/${poId}/receive?material_id=${materialId}&quantity=${quantity}`);

// PO File Upload
export const uploadPurchaseOrder = (file, supplierId, expectedDelivery, notes = '') => {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams({
    supplier_id: supplierId,
    expected_delivery: expectedDelivery
  });
  if (notes) params.append('notes', notes);
  return axios.post(`${API}/purchase-orders/upload?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const addPOItems = (poId, items) => axios.post(`${API}/purchase-orders/${poId}/items`, items);

// GRN (Goods Receipt Note)
export const getGRNs = (params) => axios.get(`${API}/grn`, { params });
export const getGRN = (id) => axios.get(`${API}/grn/${id}`);
export const createGRN = (data) => axios.post(`${API}/grn`, data);
export const receiveGRNItem = (grnId, itemData) => axios.post(`${API}/grn/${grnId}/receive`, itemData);
export const completeGRN = (grnId) => axios.post(`${API}/grn/${grnId}/complete`);

// Proforma Invoice (PI) + Material Library / Warehousing
export const extractPI = (file, supplierId) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/procurement/pi/extract?supplier_id=${supplierId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const createPI = (data) => axios.post(`${API}/procurement/pi`, data);
export const getPIs = () => axios.get(`${API}/procurement/pi`);
export const receivePIItem = (piId, itemIndex, quantityReceived, pricePerUom, gstPercent) =>
  axios.post(`${API}/procurement/pi/${piId}/receive`, { item_index: itemIndex, quantity_received: quantityReceived, price_per_uom: pricePerUom ?? null, gst_percent: gstPercent ?? 0 });
export const getLibraryMaterials = () => axios.get(`${API}/library/materials`);
export const setLibraryLocation = (libId, location) => axios.put(`${API}/library/materials/${libId}/location`, location);
export const bulkSetLibraryLocation = (ids, location) => axios.post(`${API}/library/materials/bulk-location`, { ids, location });
export const getLocationMaster = () => axios.get(`${API}/location-master`);
export const addLocationValue = (field, value, capacity, parent) => axios.post(`${API}/location-master/${field}`, { value, capacity: capacity ?? null, parent: parent ?? null });
export const deleteLocationValue = (field, value) => axios.delete(`${API}/location-master/${field}/${encodeURIComponent(value)}`);
export const consumeInventory = (batchId, quantity, reason) => axios.post(`${API}/warehouse/inventory/${batchId}/consume`, { quantity, reason });

// Movement History (audit log)
export const getMovements = (type) => axios.get(`${API}/movements`, { params: type ? { type } : {} });

// PI original file (streamed as blob so auth header is sent)
export const getPIFile = (piId) => axios.get(`${API}/procurement/pi/${piId}/file`, { responseType: 'blob' });

// Production Management (Job Work Planner)
export const extractWorkOrder = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/production-jobs/wo/extract`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const createProductionJob = (data) => axios.post(`${API}/production-jobs`, data);
export const getProductionJobs = (dispatchDay) => axios.get(`${API}/production-jobs`, { params: dispatchDay ? { dispatch_day: dispatchDay } : {} });
export const getProductionJob = (id) => axios.get(`${API}/production-jobs/${id}`);
export const linkJobPicking = (id, pickingListId) => axios.post(`${API}/production-jobs/${id}/link-picking`, { picking_list_id: pickingListId });
export const updateProdJobStage = (id, stageKey, data) => axios.patch(`${API}/production-jobs/${id}/stage/${stageKey}`, data);
export const uploadJobStageFile = (id, stageKey, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/production-jobs/${id}/stage/${stageKey}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getJobStageFile = (id, stageKey) => axios.get(`${API}/production-jobs/${id}/stage/${stageKey}/file`, { responseType: 'blob' });
export const advanceProdJobStage = (id, data) => axios.post(`${API}/production-jobs/${id}/advance`, data);
export const getMe = () => axios.get(`${API}/auth/me`);
export const getStageDurations = () => axios.get(`${API}/production-jobs/stats/stage-durations`);
export const getInsightOperators = (period) => axios.get(`${API}/production-insight/operators`, { params: period ? { period } : {} });
export const getProductionAudit = () => axios.get(`${API}/production-insight/audit`, { timeout: 90000 });
export const getAuditHistory = () => axios.get(`${API}/production-insight/history`);
export const autoGeneratePicking = (jobId) => axios.post(`${API}/production-jobs/${jobId}/auto-picking`);
export const getProductionSettings = () => axios.get(`${API}/settings/production`);
export const updateProductionSettings = (data) => axios.put(`${API}/settings/production`, data);

// Bill of Materials (BOM)
export const getBOMs = () => axios.get(`${API}/bom`);
export const createBOM = (data) => axios.post(`${API}/bom`, data);
export const updateBOM = (id, data) => axios.put(`${API}/bom/${id}`, data);
export const deleteBOM = (id) => axios.delete(`${API}/bom/${id}`);

// Picking Lists (Fabrication)
export const extractPicking = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/picking/extract`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const createPicking = (data) => axios.post(`${API}/picking`, data);
export const getPickingLists = () => axios.get(`${API}/picking`);
export const getPicking = (id) => axios.get(`${API}/picking/${id}`);
export const togglePickingLine = (id, index, picked) => axios.patch(`${API}/picking/${id}/line/${index}/toggle`, { picked });
export const completePicking = (id) => axios.post(`${API}/picking/${id}/complete`);

// Invoices
export const getInvoices = (params) => axios.get(`${API}/invoices`, { params });
export const getInvoice = (id) => axios.get(`${API}/invoices/${id}`);
export const createInvoice = (data) => axios.post(`${API}/invoices`, data);
export const updateInvoiceStatus = (id, status) => axios.patch(`${API}/invoices/${id}/status?status=${status}`);
export const recordPayment = (invoiceId, data) => axios.post(`${API}/invoices/${invoiceId}/payments`, data);
export const getPayments = (invoiceId) => axios.get(`${API}/invoices/${invoiceId}/payments`);

// Users
export const getUsers = () => axios.get(`${API}/users`);
export const updateUserRole = (id, role) => axios.put(`${API}/users/${id}/role?role=${role}`);
export const updateUserStages = (id, stages) => axios.put(`${API}/users/${id}/stages`, { stages });

// OTP Authentication
export const requestOTP = (data) => axios.post(`${API}/auth/request-otp`, data);
export const verifyOTP = (data) => axios.post(`${API}/auth/verify-otp`, data);

// Password Reset
export const forgotPassword = (data) => axios.post(`${API}/auth/forgot-password`, data);
export const resetPassword = (data) => axios.post(`${API}/auth/reset-password`, data);

// ========== COMPREHENSIVE FINANCE ==========
// Chart of Accounts
export const getChartOfAccounts = (params) => axios.get(`${API}/finance/accounts`, { params });
export const createChartOfAccount = (data) => axios.post(`${API}/finance/accounts`, data);
export const seedDefaultAccounts = () => axios.post(`${API}/finance/accounts/seed-defaults`);

// Vendors
export const getFinanceVendors = () => axios.get(`${API}/finance/vendors`);
export const createFinanceVendor = (data) => axios.post(`${API}/finance/vendors`, data);

// Bills (Accounts Payable)
export const getFinanceBills = (params) => axios.get(`${API}/finance/bills`, { params });
export const createFinanceBill = (data) => axios.post(`${API}/finance/bills`, data);
export const recordBillPayment = (billId, data) => axios.post(`${API}/finance/bills/${billId}/payment`, data);

// Customer Invoices (Accounts Receivable)
export const getCustomerInvoices = (params) => axios.get(`${API}/finance/customer-invoices`, { params });
export const createCustomerInvoice = (data) => axios.post(`${API}/finance/customer-invoices`, data);
export const recordCustomerPayment = (invoiceId, data) => axios.post(`${API}/finance/customer-invoices/${invoiceId}/payment`, data);

// Bank Accounts
export const getBankAccounts = () => axios.get(`${API}/finance/bank-accounts`);
export const createBankAccount = (data) => axios.post(`${API}/finance/bank-accounts`, data);
export const updateBankAccount = (accountId, data) => axios.patch(`${API}/finance/bank-accounts/${accountId}`, data);
export const deleteBankAccount = (accountId) => axios.delete(`${API}/finance/bank-accounts/${accountId}`);

// Project Budget
export const getProjectBudget = (projectId) => axios.get(`${API}/finance/project-budget/${projectId}`);
export const createProjectBudget = (data) => axios.post(`${API}/finance/project-budget`, data);

// MIS Reports
export const getProjectPnL = (projectId) => axios.get(`${API}/finance/reports/project-pnl/${projectId}`);
export const getBudgetVsActual = (projectId) => axios.get(`${API}/finance/reports/budget-vs-actual/${projectId}`);
export const getVendorSummary = () => axios.get(`${API}/finance/reports/vendor-summary`);
export const getReceivablesAging = () => axios.get(`${API}/finance/reports/receivables-aging`);
export const getProjectCashFlow = (projectId) => axios.get(`${API}/finance/reports/cash-flow/${projectId}`);
export const getFinanceDashboard = () => axios.get(`${API}/finance/dashboard`);

// EVM Work Packages (for finance integration)
export const getEVMWorkPackages = (projectId) => axios.get(`${API}/evm/projects/${projectId}/work-packages`);

// Project WBS Finance Tracking
export const getProjectWBSTracking = (projectId) => axios.get(`${API}/finance/project/${projectId}/wbs-tracking`);
export const getProjectWorkPackages = (projectId) => axios.get(`${API}/evm/projects/${projectId}/work-packages`);

// Document AI Extraction
export const extractDocumentData = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/finance/documents/extract`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// AI
export const getAISuggestion = (data) => axios.post(`${API}/ai/suggest`, data);
export const optimizeQuotation = (quotationId) => axios.post(`${API}/ai/optimize-quotation?quotation_id=${quotationId}`);

// File Upload
export const uploadDesignFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/upload/design`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getUploadedFiles = () => axios.get(`${API}/upload/files`);

// ========== WAREHOUSE MANAGEMENT ==========

// Master Catalog
export const getWarehouseCatalog = (params) => axios.get(`${API}/warehouse/catalog`, { params });
export const getCatalogItem = (id) => axios.get(`${API}/warehouse/catalog/${id}`);
export const createCatalogItem = (data) => axios.post(`${API}/warehouse/catalog`, data);
export const updateCatalogItem = (id, data) => axios.put(`${API}/warehouse/catalog/${id}`, data);

// Live Inventory
export const getWarehouseInventory = (params) => axios.get(`${API}/warehouse/inventory`, { params });
export const getInventoryBatch = (batchId) => axios.get(`${API}/warehouse/inventory/${batchId}`);
export const createInventoryEntry = (data) => axios.post(`${API}/warehouse/inventory`, data);
export const updateInventoryStatus = (batchId, status, notes) => 
  axios.patch(`${API}/warehouse/inventory/${batchId}/status?status=${status}${notes ? '&notes=' + encodeURIComponent(notes) : ''}`);
export const updateQCStatus = (batchId, qcPassed, notes) => 
  axios.patch(`${API}/warehouse/inventory/${batchId}/qc?qc_passed=${qcPassed}${notes ? '&qc_notes=' + encodeURIComponent(notes) : ''}`);

// Cutting Operations
export const processCuttingList = (data) => axios.post(`${API}/warehouse/cutting`, data);
export const uploadCuttingList = (file, bladeKerfMm = 3.0, endTrimMm = 5.0, jobId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams({
    blade_kerf_mm: bladeKerfMm.toString(),
    end_trim_mm: endTrimMm.toString()
  });
  if (jobId) params.append('job_id', jobId);
  return axios.post(`${API}/warehouse/cutting/upload?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getCuttingHistory = (params) => axios.get(`${API}/warehouse/cutting/history`, { params });
export const getCuttingTemplateUrl = () => `${API}/warehouse/cutting/template`;

// Remnants
export const getWarehouseRemnants = (params) => axios.get(`${API}/warehouse/remnants`, { params });
export const createRemnant = (data) => axios.post(`${API}/warehouse/remnants`, data);
export const consumeRemnant = (remnantId) => axios.patch(`${API}/warehouse/remnants/${remnantId}/consume`);

// Finished Goods
export const getFinishedGoods = (params) => axios.get(`${API}/warehouse/finished-goods`, { params });
export const createFinishedGood = (data) => axios.post(`${API}/warehouse/finished-goods`, data);

// Stillage
export const getStillages = (params) => axios.get(`${API}/warehouse/stillage`, { params });
export const createStillage = (data) => axios.post(`${API}/warehouse/stillage`, data);
export const addItemToStillage = (stillageId, fgId) => axios.post(`${API}/warehouse/stillage/${stillageId}/add-item?fg_id=${fgId}`);
export const dispatchStillage = (stillageId) => axios.post(`${API}/warehouse/stillage/${stillageId}/dispatch`);

// Warehouse Stats & Locations
export const getWarehouseStats = () => axios.get(`${API}/warehouse/stats`);
export const getWarehouseLocations = () => axios.get(`${API}/warehouse/locations`);

// QR Code
export const getQRCodeUrl = (itemType, itemId) => `${API}/warehouse/qr/${itemType}/${itemId}`;

// ========== PRODUCTION MODULE (FACTORY FLOOR) ==========
// Catalog
export const seedProductionCatalog = () => axios.post(`${API}/factory/catalog/seed`);
export const getProfileCatalog = (params) => axios.get(`${API}/factory/catalog/profiles`, { params });
export const getHardwareCatalog = (params) => axios.get(`${API}/factory/catalog/hardware`, { params });
export const getMachineStations = () => axios.get(`${API}/factory/machines`);

// Cutting List
export const generateCuttingList = (data) => axios.post(`${API}/factory/cutting-list/generate`, data);
export const getCuttingList = (jobId) => axios.get(`${API}/factory/cutting-list/${jobId}`);
export const getAllCuttingLists = (params) => axios.get(`${API}/factory/cutting-lists`, { params });
export const addCustomCuttingItem = (clId, item) => axios.post(`${API}/factory/cutting-list/${clId}/add-item`, item);

// Custom Cut List Upload
export const uploadCustomCutlist = (formData) => axios.post(`${API}/factory/custom-cutlist/upload`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Joinery Drawings
export const uploadJoineryDrawing = (formData) => axios.post(`${API}/factory/joinery-drawings/upload`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getJoineryDrawings = (jobId) => axios.get(`${API}/factory/joinery-drawings/${jobId}`);
export const deleteJoineryDrawing = (drawingId) => axios.delete(`${API}/factory/joinery-drawings/${drawingId}`);

// Custom Profiles
export const addCustomProfile = (data) => axios.post(`${API}/factory/catalog/custom-profile`, data);
export const updateProfileLengths = (profileId, lengths) => axios.patch(`${API}/factory/catalog/profiles/${profileId}/lengths`, lengths);

// Production Batches
export const createProductionBatch = (data) => axios.post(`${API}/factory/batches`, data);
export const getProductionBatches = (params) => axios.get(`${API}/factory/batches`, { params });
export const getProductionBatch = (batchId) => axios.get(`${API}/factory/batches/${batchId}`);

// Work Orders
export const updateWorkOrderStatus = (woId, status, operator) => 
  axios.patch(`${API}/factory/work-orders/${woId}/status?status=${status}${operator ? '&operator=' + operator : ''}`);
export const completeWorkOrderItem = (woId, itemId) => 
  axios.post(`${API}/factory/work-orders/${woId}/complete-item?item_id=${itemId}`);

// Kanban & Dashboard
export const getProductionKanban = () => axios.get(`${API}/factory/kanban`);
export const getProductionDashboard = () => axios.get(`${API}/factory/dashboard`);

// Machine Management
export const createMachine = (data) => axios.post(`${API}/factory/machines`, data);
export const updateMachine = (id, data) => axios.put(`${API}/factory/machines/${id}`, data);
export const updateMachineStatus = (id, status) => axios.patch(`${API}/factory/machines/${id}/status`, { status });
export const deleteMachine = (id) => axios.delete(`${API}/factory/machines/${id}`);
export const logMaintenance = (machineId, data) => axios.post(`${API}/factory/machines/${machineId}/maintenance`, data);
export const getMaintenanceLogs = (machineId) => axios.get(`${API}/factory/machines/${machineId}/maintenance`);

// Reports
export const getDailyProductionSummary = (days) => axios.get(`${API}/factory/reports/daily-summary`, { params: { days } });
export const getMaterialWastageReport = (days) => axios.get(`${API}/factory/reports/material-wastage`, { params: { days } });



// ==================== WMS (PostgreSQL) ====================

// WMS Branches
export const getWMSBranches = () => axios.get(`${API}/wms/branches`);
export const createWMSBranch = (data) => axios.post(`${API}/wms/branches`, data);

// WMS Racks
export const getWMSRacks = (branchId) => axios.get(`${API}/wms/racks`, { params: branchId ? { branch_id: branchId } : {} });
export const createWMSRack = (data) => axios.post(`${API}/wms/racks`, data);
export const getRackBins = (rackId) => axios.get(`${API}/wms/racks/${rackId}/bins`);
export const generateRackBins = (rackId) => axios.post(`${API}/wms/racks/${rackId}/generate-bins`);

// WMS Bins
export const createWMSBin = (data) => axios.post(`${API}/wms/bins`, data);

// WMS Inventory Items
export const getWMSItems = (params) => axios.get(`${API}/wms/items`, { params });
export const createWMSItem = (data) => axios.post(`${API}/wms/items`, data);
export const updateWMSItem = (id, data) => axios.put(`${API}/wms/items/${id}`, data);

// WMS Stock
export const getWMSStock = (params) => axios.get(`${API}/wms/stock`, { params });
export const receiveWMSStock = (data) => axios.post(`${API}/wms/stock/receive`, data);
export const consumeWMSStock = (data) => axios.post(`${API}/wms/stock/consume`, data);
export const transferWMSStock = (data) => axios.post(`${API}/wms/stock/transfer`, data);

// WMS Machines
export const getWMSMachines = (params) => axios.get(`${API}/wms/machines`, { params });
export const createWMSMachine = (data) => axios.post(`${API}/wms/machines`, data);
export const updateWMSMachineStatus = (id, status) => axios.put(`${API}/wms/machines/${id}/status?status=${status}`);

// WMS Dashboard
export const getWMSDashboard = () => axios.get(`${API}/wms/dashboard`);

// ==================== Production Floor (PostgreSQL) ====================

// Production Orders
export const getFloorOrders = (params) => axios.get(`${API}/floor/orders`, { params });
export const createFloorOrder = (data) => axios.post(`${API}/floor/orders`, data);
export const getFloorOrder = (id) => axios.get(`${API}/floor/orders/${id}`);
export const releaseFloorOrder = (id) => axios.post(`${API}/floor/orders/${id}/release`);

// Production Items
export const updateFloorItemStage = (itemId, data) => axios.put(`${API}/floor/items/${itemId}/stage`, data);
export const completeFloorItem = (itemId) => axios.post(`${API}/floor/items/${itemId}/complete`);

// Workflows
export const getFloorWorkflows = () => axios.get(`${API}/floor/workflows`);
export const getFloorDashboard = () => axios.get(`${API}/floor/dashboard`);
